import { isPS } from '@/utils';
import { isEqual } from 'lodash-es';
import type { z } from 'zod';
import type { QueryStoreQueryModel } from '../_models/query-store';
import { generateQueryAndLeafs } from './query-store';

type Query = z.infer<typeof QueryStoreQueryModel>;

type AnalysisCascaderComparisonResult = {
  hasChanged: boolean;
  isDifferent: boolean;
  shouldCreateNewCascader: boolean;
  newCascaderId: string | undefined;
};

type AnalysisCascaderComparisonParams = {
  values: {
    nestedOtherResponsibleOrganizationIds?: string[][] | null;
    nestedOrganizationIds?: string[][] | null;
    nestedPolicyIds?: string[][] | null;
    nestedExpenditureBudgetPurposeIds?: string[][] | null;
  };
  currentCascaderValues: Partial<Query>;
};

/**
 * 分析機能の検索条件の変更を検知し、新しいcascaderIdが必要かどうかを判定する
 */
export const compareAnalysisCascaderValues = ({
  values,
  currentCascaderValues,
}: AnalysisCascaderComparisonParams): {
  newQueryStore: ReturnType<typeof generateQueryAndLeafs>;
  comparisonResult: Omit<AnalysisCascaderComparisonResult, 'newCascaderId'>;
} => {
  const newQueryStore = generateQueryAndLeafs({
    otherResponsibleOrganizationIds:
      values.nestedOtherResponsibleOrganizationIds,
    organizationIds: values.nestedOrganizationIds,
    policyIds: values.nestedPolicyIds,
    expenditureBudgetPurposeIds: values.nestedExpenditureBudgetPurposeIds,
  });

  const hasChanged = Object.keys(currentCascaderValues).some((key) => {
    const typedKey = key as keyof Query;
    const newQuery = newQueryStore?.query?.[typedKey];
    const oldQuery = currentCascaderValues[typedKey];

    // 配列の長さが異なる場合も変更として検知
    return (
      (newQuery && !isEqual(newQuery, oldQuery)) ||
      oldQuery?.length !== newQuery?.length
    );
  });

  const isDifferent =
    !isPS() &&
    hasChanged &&
    !isEqual(newQueryStore?.query, currentCascaderValues);

  const shouldCreateNewCascader = isDifferent && !!newQueryStore;

  return {
    newQueryStore,
    comparisonResult: {
      hasChanged,
      isDifferent,
      shouldCreateNewCascader,
    },
  };
};

/**
 * 分析機能の検索条件を比較し、必要に応じて新しいcascaderIdを作成する
 */
export const processAnalysisCascaderComparison = async ({
  values,
  currentCascaderValues,
  prevCascaderId,
  createQueryStore,
}: {
  values: AnalysisCascaderComparisonParams['values'];
  currentCascaderValues: Partial<Query>;
  prevCascaderId?: string | null;
  createQueryStore: (
    queryStore: ReturnType<typeof generateQueryAndLeafs>,
  ) => Promise<{ id: string }>;
}): Promise<{
  newCascadeId: string | undefined;
}> => {
  // PS環境では常に新しいcascaderIdを作成しないため、早期リターン
  if (isPS()) {
    const newQueryStore = generateQueryAndLeafs({
      otherResponsibleOrganizationIds:
        values.nestedOtherResponsibleOrganizationIds,
      organizationIds: values.nestedOrganizationIds,
      policyIds: values.nestedPolicyIds,
      expenditureBudgetPurposeIds: values.nestedExpenditureBudgetPurposeIds,
    });

    return {
      newCascadeId: newQueryStore ? (prevCascaderId ?? undefined) : undefined,
    };
  }

  const { newQueryStore, comparisonResult } = compareAnalysisCascaderValues({
    values,
    currentCascaderValues,
  });

  const newCascaderId = comparisonResult.shouldCreateNewCascader
    ? (await createQueryStore(newQueryStore)).id
    : undefined;

  const newCascadeId = determineAnalysisCascaderId(
    comparisonResult.shouldCreateNewCascader,
    newQueryStore,
    prevCascaderId,
    newCascaderId,
  );

  return {
    newCascadeId,
  };
};

/**
 * 分析機能用の新しいcascaderIdを決定する
 */
export const determineAnalysisCascaderId = (
  shouldCreateNewCascader: boolean,
  newQueryStore: ReturnType<typeof generateQueryAndLeafs>,
  prevCascaderId?: string | null,
  newCascaderId?: string,
): string | undefined => {
  if (shouldCreateNewCascader && newCascaderId) {
    return newCascaderId;
  }
  if (newQueryStore) {
    return prevCascaderId ?? undefined;
  }
  return undefined;
};
